/**
 * Shared Polish label mappings for billing enum values.
 *
 * Single source of truth for tenant-facing and admin-facing modals.
 * Mirrors the backend `OrderLinePricingType` enum in
 * `apps/backend/src/billing/enums/index.ts` (NOT_PRICED_YET, ESTIMATED,
 * CALCULATED, ARBITRARY).
 *
 * @see docs/specifications/2026-05-12-admin-manual-pricing-ui/spec.md (A1, C2, M4)
 */

/**
 * Frontend mirror of backend `OrderLinePricingType` enum.
 * Keep in sync with `apps/backend/src/billing/enums/index.ts:OrderLinePricingType`.
 */
export type OrderLinePricingType =
  | 'NOT_PRICED_YET'
  | 'ESTIMATED'
  | 'CALCULATED'
  | 'ARBITRARY';

/**
 * Polish labels for `OrderLinePricingType`. `satisfies` ensures the record
 * stays exhaustive — adding a new enum value without a label is a compile error.
 */
export const PRICING_TYPE_LABELS = {
  NOT_PRICED_YET: 'Nie wyceniono',
  ESTIMATED: 'Szacunkowa',
  CALCULATED: 'Wyliczona',
  ARBITRARY: 'Cena indywidualna',
} as const satisfies Record<OrderLinePricingType, string>;

/**
 * Resolve a Polish label for a pricing type. Accepts `string` (defensive)
 * because backend response types may widen the enum to `string`; falls back
 * to the raw value if unknown.
 */
export const getPricingLabel = (type: string): string =>
  PRICING_TYPE_LABELS[type as OrderLinePricingType] ?? type;

/**
 * Frontend mirror of backend `OrderTrigger` enum
 * (`apps/backend/src/billing/enums/index.ts:OrderTrigger`).
 *
 * @see AC-6 in docs/specifications/2026-09-07-proforma-reczny-termin-platnosci/spec.md
 */
export type OrderTrigger =
  | 'MANUAL'
  | 'AUTO_RENEWAL'
  | 'SELF_SERVICE'
  | 'STRIPE_WEBHOOK'
  | 'MIGRATION';

/** Polish labels for `OrderTrigger` — used in the order collision warning list (AC-6). */
export const ORDER_TRIGGER_LABELS = {
  MANUAL: 'Ręczne (admin)',
  AUTO_RENEWAL: 'Automatyczne odnowienie',
  SELF_SERVICE: 'Self-service',
  STRIPE_WEBHOOK: 'Stripe',
  MIGRATION: 'Migracja',
} as const satisfies Record<OrderTrigger, string>;

/** Resolve a Polish label for an order trigger. Falls back to the raw value if unknown. */
export const getTriggerLabel = (trigger: string): string =>
  ORDER_TRIGGER_LABELS[trigger as OrderTrigger] ?? trigger;
